import { useState, useEffect, useCallback } from "react";
import { Search, Check, Trash2, Volume2, Telescope, Brain } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { srDisplay } from "@/lib/serbianScript";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import { useI18n } from "@/context/I18nContext";

const clips = new Map(); // session memory: repeat press never hits the server
const SOURCES = ["all", "languageloop", "roommate", "quizforge", "manual"];
// Presentation-only labels for stored source ids; unknown ids show as stored.
const NAV_SRC = { languageloop: "nav.languageloop", roommate: "nav.roommate", quizforge: "nav.quizforge", today: "nav.today", voice_journal: "nav.journal", fluent_friend: "nav.friend", story: "nav.story", accentcoach: "nav.accentcoach", debategym: "nav.debategym" };
const OWN_SRC = ["manual", "mission", "word_explorer", "object_lens", "scene_lens", "text_lens", "fluent_friend_live"];
const srcLabel = (t, s) => (s === "all" ? t("vocab.all") : NAV_SRC[s] ? t(NAV_SRC[s]) : OWN_SRC.includes(s) ? t(`vocabSrc.${s}`) : s);

export default function Vocab() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("all");
  const [filter, setFilter] = useState("all");
  const [due, setDue] = useState(0);
  const navigate = useNavigate();
  const explore = (w, id) => navigate(`/vocab/explore?q=${encodeURIComponent(w)}${id ? `&vocab=${encodeURIComponent(id)}` : ""}`);
  const typed = q.trim();
  const exactSaved = items.some((it) => it.word.toLowerCase() === typed.toLowerCase());

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (source !== "all") params.source = source;
    if (filter !== "all") params.known = filter === "known" ? "true" : "false";
    const res = await api.get("/vocab", { params });
    setItems(res.data);
  }, [q, source, filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/nudges").then((r) => setDue(r.data.words_due || 0)).catch(() => {}); }, [user?.active_learning_profile_id]);

  const toggleKnown = async (item) => {
    await api.patch(`/vocab/${item.id}/known`, { known: !item.known });
    load();
  };
  const remove = async (item) => {
    await api.delete(`/vocab/${item.id}`);
    toast.success(t("vocab.removed"));
    load();
  };
  const [voiceFailedId, setVoiceFailedId] = useState(null);
  const speak = async (item) => {
    setVoiceFailedId(null);
    try {
      if (!clips.has(item.id)) {
        const r = await api.post(`/vocab/${item.id}/audio`, null, { responseType: "blob" });
        clips.set(item.id, URL.createObjectURL(r.data));
      }
      await new Audio(clips.get(item.id)).play();
    } catch { setVoiceFailedId(item.id); }
  };

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="font-cabinet font-extrabold text-3xl">{t("vocab.title")}</h1>
            <p className="text-gray-600">{t("vocab.subtitle")}</p>
          </div>
          <button onClick={() => navigate("/review")} className="nb-btn-primary px-4 py-3 shrink-0" data-testid="vocab-review-btn">
            <Brain className="w-5 h-5" /> {t("review.title")}{due > 0 && <span className="ml-1 bg-white text-ink border-2 border-ink rounded-full px-2 text-xs" data-testid="vocab-review-due">{due}</span>}
          </button>
        </div>

        <div className="flex flex-wrap gap-3 mt-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-5 h-5 absolute left-3 top-3.5 text-gray-400" />
            <input className="nb-input pl-10" placeholder={t("vocab.searchWords")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="vocab-search" />
          </div>
          <select className="nb-input w-auto" value={source} onChange={(e) => setSource(e.target.value)} data-testid="vocab-source">
            {SOURCES.map((s) => <option key={s} value={s}>{srcLabel(t, s)}</option>)}
          </select>
          <select className="nb-input w-auto" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="vocab-filter">
            <option value="all">{t("vocab.all")}</option><option value="known">{t("vocab.known")}</option><option value="learning">{t("vocab.learning")}</option>
          </select>
        </div>

        {typed && !exactSaved && typed.split(/\s+/).length <= 4 && (
          <button onClick={() => explore(typed)} className="nb-btn-outline mt-4 px-4 py-2 text-sm inline-flex" data-testid="vocab-explore-query">
            <Telescope className="w-4 h-4" /> {t("wordExplorer.exploreWord").replace("{word}", typed)}
          </button>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {items.map((it) => (
            <div key={it.id} className={`nb-card p-4 ${it.known ? "bg-mint/40" : "bg-white"}`} data-testid="vocab-item">
              <div className="flex items-start justify-between">
                <div className="font-cabinet font-extrabold text-xl" dir="auto" data-testid="vocab-word">{user?.target_lang === "sr" ? srDisplay(it.word, user.target_script) : it.word}</div>
                {it.can_listen && <button onClick={() => speak(it)} className="text-gray-400 hover:text-ink" data-testid="vocab-listen"><Volume2 className="w-4 h-4" /></button>}
              </div>
              {voiceFailedId === it.id && <p className="text-xs text-gray-500" data-testid="vocab-voice-unavailable">{t("friend.live.voiceUnavailable")}</p>}
              <div className="text-sm text-gray-600" dir="auto">{it.translation}</div>
              {it.example_sentence && <p className="text-xs text-gray-500 mt-2 italic" dir="auto">"{it.example_sentence}"</p>}
              <div className="flex items-center justify-between mt-3">
                <span className="nb-tag bg-paper text-[10px]">{srcLabel(t, it.source)}</span>
                <div className="flex gap-2">
                  {it.word.split(/\s+/).length <= 4 && <button onClick={() => explore(it.word, it.id)} className="h-7 px-2 rounded-lg border-2 border-ink bg-white text-xs font-bold flex items-center gap-1 hover:bg-sky" title={t("wordExplorer.explore")} data-testid="vocab-explore-btn"><Telescope className="w-3.5 h-3.5" /> {t("wordExplorer.explore")}</button>}
                  <button onClick={() => toggleKnown(it)} className={`w-7 h-7 rounded-lg border-2 border-ink flex items-center justify-center ${it.known ? "bg-mint" : "bg-white"}`} title={t("vocab.markKnown")} data-testid="vocab-known-btn"><Check className="w-4 h-4" /></button>
                  <button onClick={() => remove(it)} className="w-7 h-7 rounded-lg border-2 border-ink bg-white flex items-center justify-center hover:bg-red-100" data-testid="vocab-delete-btn"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
        {items.length === 0 && <p className="text-center text-gray-400 py-16">{t("vocab.empty")}</p>}
      </div>
    </AppLayout>
  );
}
